// src/pages/About.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/autoplay';

// === IMAGES ===
import heroBg from '../assets/Resort/76.jpeg';
import slide1 from '../assets/Ayurveda/8.jpeg';
import slide2 from '../assets/Resort/94.jpeg';
import slide3 from '../assets/Resort/95.jpeg';
import slide4 from '../assets/Ayurveda/56.jpeg';
import slide5 from '../assets/Resort/88.jpeg';
import historyImage from '../assets/Resort/105.jpeg';
import chairmanImage from '../assets/WhatsApp Image 2026-02-06 at 22.34.27.jpeg';
import staffImage from '../assets/Resort/134.jpeg';
import contactBgImage from '../assets/Resort/76.jpeg';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};
const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const About = () => {
    const { t } = useTranslation('about');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, []);

    const getHeroImagesSlider = (t) => [
        { image: slide1, title: t('slide1') },
        { image: slide2, title: t('slide2') },
        { image: slide3, title: t('slide3') },
        { image: slide4, title: t('slide4') },
        { image: slide5, title: t('slide5') },
    ];

    const heroImagesSlider = getHeroImagesSlider(t);

    return (
        <div className="bg-black">
            {/* ===== HERO SECTION ===== */}
            {/* ===== HERO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative max-h-[1600px] w-full bg-cover bg-center  md:max-h-[1140px] lg:max-h-[1100px] xl:max-h-[1500px]"
                style={{
                    backgroundImage: `
            linear-gradient(0deg, rgba(0, 0, 0, 0.67) 32.63%, rgba(0, 0, 0, 0.18) 63.34%),
            url(${heroBg})
        `,
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="relative w-full">
                        <div className="relative w-fit pt-[120px] md:pt-[100px] lg:pt-[120px] xl:pt-[150px]">
                            <h1 className="font-bold leading-[60px] md:leading-[47px] lg:leading-[5.5rem] text-white">
                                <span className="text-[2.5rem] md:text-5xl lg:text-7xl xl:text-[6.25rem]">
                                    {t('heroTitle')}
                                </span>
                                <br />
                                <span className="text-[2.75rem] md:text-[3.5rem] lg:text-[5rem] xl:text-[6.5rem]">
                                    {t('heroSubtitle')}
                                </span>
                                <br />
                                <span className="text-[2.75rem] md:text-[3.5rem] lg:text-[5rem] xl:text-[6.5rem]">
                                    {t('heroBeachResort')}
                                </span>
                            </h1>

                            <div className="absolute h-[5px] w-[110px] bg-white md:h-[4px] lg:h-[5px] lg:w-[230px] bottom-[-20px] left-0"></div>
                        </div>

                        {/* The rest remains unchanged – slider + text blocks */}
                        <div className="flex w-full flex-col md:items-end md:justify-end">

                            <>
                                {/* Desktop & Tablet */}
                                <div className="relative mt-10 hidden h-[210px] w-[580px] overflow-hidden rounded-lg shadow-lg md:block lg:h-[280px] lg:w-[850px] xl:h-[330px] xl:w-[920px]">
                                    <Swiper
                                        modules={[Autoplay]}
                                        slidesPerView={4}
                                        spaceBetween={12}
                                        loop
                                        speed={500}
                                        autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                        breakpoints={{
                                            768: { slidesPerView: 3 },
                                            1024: { slidesPerView: 4 },
                                        }}
                                        href="/beach/contact-us"
                                    >
                                        {heroImagesSlider.map((slide, i) => (
                                            <SwiperSlide key={i} className="!h-full">
                                                <div className="relative h-full w-full">
                                                    <img
                                                        src={slide.image}
                                                        alt={slide.title}
                                                        className="h-full w-full rounded-[5px] object-cover"
                                                    />
                                                    <h4 className="absolute bottom-5 left-5 max-w-[80px] font-medium text-white text-lg lg:max-w-[130px] lg:text-xl xl:max-w-[150px]">
                                                        {slide.title}
                                                    </h4>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>


                                <div className="relative mt-10 h-[280px] w-full overflow-hidden rounded-lg shadow-lg md:hidden">
                                    <Swiper
                                        modules={[Autoplay]}
                                        slidesPerView={2}
                                        spaceBetween={12}
                                        loop
                                        speed={500}
                                        autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                        className="h-full"
                                    >
                                        {heroImagesSlider.map((slide, i) => (
                                            <SwiperSlide key={i} className="!h-full">
                                                <div className="relative h-full w-full">
                                                    <img
                                                        src={slide.image}
                                                        alt={slide.title}
                                                        className="h-full w-full rounded-[5px] object-cover"
                                                    />
                                                    <h4 className="absolute bottom-3 left-5 font-medium text-white text-xl">
                                                        {slide.title}
                                                    </h4>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </>

                            {/* Text blocks */}
                            <div className="relative z-10 hidden w-[210px] text-right md:block lg:w-[334px]">
                                <p className="mt-8 text-white text-sm font-medium lg:text-base xl:mt-10">
                                    {t('heroTagline1')}
                                </p>
                                <div className="absolute -bottom-3 right-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>

                            <div className="relative bottom-7 left-0 z-10 mt-14 w-[244px] text-left md:absolute md:mt-10 md:w-[205px] lg:bottom-28 lg:w-[320px] xl:w-[353px]">
                                <p className="text-white text-xs leading-5 font-medium md:text-sm md:leading-normal lg:text-base">
                                    {t('heroTagline2')}
                                </p>
                                <div className="absolute -bottom-3 left-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>

                            <div className="relative z-10 flex w-full items-end justify-end text-right md:hidden lg:w-[334px]">
                                <p className="mt-8 w-[220px] text-white text-xs leading-5 font-medium md:text-sm lg:text-base xl:mt-10">
                                    {t('heroTagline1')}
                                </p>
                                <div className="absolute -bottom-3 right-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== HISTORY SECTION ===== */}
            <section className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24">
                <div className="container mx-auto px-4">

                    {/* === MOBILE / SM / MD layout (stacked: label → title → image → text) === */}
                    <div className="block lg:hidden space-y-8">
                        {/* Label */}
                        <div className="flex items-center gap-4">
                            <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                            <span className="text-[14px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                {t('historyLabel')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-[32px] sm:text-[40px] md:text-[48px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                            {t('historyTitle')}
                        </h2>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp} // assuming you have fadeInUp variant
                            className="relative rounded-2xl overflow-hidden shadow-2xl group"
                        >
                            <div className="relative aspect-[4/3] sm:aspect-[3/2] md:aspect-video">
                                <img
                                    src={historyImage}
                                    alt="Historical view of Muthumuni Ayurveda Beach Resort"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Our+History'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                                {/* Floating Info Card - smaller version for mobile */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4, duration: 0.7 }}
                                    className="absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[11px] sm:text-[12px] uppercase tracking-wider text-[#BF943A] font-semibold mb-1">
                                                {t('established')}
                                            </p>
                                            <p className="text-[18px] sm:text-[20px] font-bold text-white">{t('since2010')}</p>
                                        </div>
                                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#BF943A] to-[#D4A574] flex items-center justify-center">
                                            <span className="text-[26px]">🏛️</span>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Corner decorations - smaller */}
                                <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-[#BF943A] rounded-tr-2xl"></div>
                                <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-[#BF943A] rounded-bl-2xl"></div>
                            </div>
                        </motion.div>

                        {/* Description + Stats + Buttons */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp}
                            className="space-y-6"
                        >
                            <div className="space-y-4 text-gray-300">
                                <p className="text-[16px] md:text-[18px] leading-relaxed">
                                    {t('historyDesc1')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('historyDesc2')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('historyDesc3')}
                                </p>
                            </div>

                            <div className="pt-4 grid grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <div className="text-[32px] sm:text-[36px] font-bold text-[#BF943A]">{t('statYear')}</div>
                                    <p className="text-[13px] sm:text-[15px] text-gray-400">{t('statYearLabel')}</p>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-[32px] sm:text-[36px] font-bold text-[#96BA33]">{t('statGuest')}</div>
                                    <p className="text-[13px] sm:text-[15px] text-gray-400">{t('statGuestLabel')}</p>
                                </div>
                            </div>

                            <div className="pt-4 flex flex-wrap gap-4">
                                <a
                                    href="/beach/home"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF943A] to-[#D4A574] hover:from-[#D4A574] hover:to-[#BF943A] text-black font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg text-[14px] sm:text-[16px] uppercase tracking-wider"
                                >
                                    {t('experienceLegacy')}
                                    <span>→</span>
                                </a>
                                <a
                                    href="/beach/contact-us"
                                    className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-all duration-300 text-[14px] sm:text-[16px] uppercase tracking-wider"
                                >
                                    {t('contactUs')}
                                </a>
                            </div>
                        </motion.div>
                    </div>

                    {/* === DESKTOP / LG layout (side by side) === */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Left Side - Description */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInLeft}
                                className="space-y-6 order-2 lg:order-1"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                                    <span className="text-[16px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                        {t('historyLabel')}
                                    </span>
                                </div>

                                <h2 className="text-[48px] lg:text-[60px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                                    {t('historyTitle')}
                                </h2>

                                <div className="space-y-4 text-gray-300">
                                    <p className="text-[18px] lg:text-[20px] leading-relaxed">
                                        {t('historyDesc1')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('historyDesc2')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('historyDesc3')}
                                    </p>
                                </div>

                                <div className="pt-4 grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <div className="text-[42px] font-bold text-[#BF943A]">{t('statYear')}</div>
                                        <p className="text-[16px] text-gray-400">{t('statYearLabel')}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-[42px] font-bold text-[#96BA33]">{t('statGuest')}</div>
                                        <p className="text-[16px] text-gray-400">{t('statGuestLabel')}</p>
                                    </div>
                                </div>

                                <div className="pt-6 flex flex-wrap gap-4">
                                    <a
                                        href="/beach/rooms"
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF943A] to-[#D4A574] hover:from-[#D4A574] hover:to-[#BF943A] text-black font-bold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl text-[16px] uppercase tracking-wider"
                                    >
                                        {t('experienceLegacy')}
                                        <span>→</span>
                                    </a>
                                    <a
                                        href="/beach/contact-us"
                                        className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 text-[16px] uppercase tracking-wider"
                                    >
                                        {t('contactUs')}
                                    </a>
                                </div>
                            </motion.div>

                            {/* Right Side - Image */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInRight}
                                className="relative order-1 lg:order-2"
                            >
                                <div className="relative h-[500px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                    <img
                                        src={historyImage}
                                        alt="Historical view of Muthumuni Ayurveda Beach Resort"
                                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Our+History'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                    {/* Floating Info Card */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.5, duration: 0.8 }}
                                        className="absolute bottom-8 left-8 right-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[14px] uppercase tracking-wider text-[#BF943A] font-semibold mb-1">
                                                    {t('established')}
                                                </p>
                                                <p className="text-[24px] font-bold text-white">{t('since2010')}</p>
                                            </div>
                                            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#BF943A] to-[#D4A574] flex items-center justify-center">
                                                <span className="text-[32px]">🏛️</span>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Decorative Corner Elements */}
                                    <div className="absolute top-6 right-6 w-20 h-20 border-t-2 border-r-2 border-[#BF943A] rounded-tr-3xl"></div>
                                    <div className="absolute bottom-6 left-6 w-20 h-20 border-b-2 border-l-2 border-[#BF943A] rounded-bl-3xl"></div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ===== CHAIRMAN'S MESSAGE ===== */}
            <section className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24">
                <div className="container mx-auto px-4">

                    {/* === MOBILE / SM / MD layout (stacked: label → title → image → message) === */}
                    <div className="block lg:hidden space-y-8">
                        {/* Label */}
                        <div className="flex items-center gap-4">
                            <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                            <span className="text-[14px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                {t('chairmanLabel')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-[32px] sm:text-[40px] md:text-[48px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                            {t('chairmanTitle')}
                        </h2>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp} // assuming fadeInUp variant exists
                            className="relative rounded-2xl overflow-hidden shadow-2xl group"
                        >
                            <div className="relative aspect-[4/3] sm:aspect-[3/2] md:aspect-video">
                                <img
                                    src={chairmanImage}
                                    alt={t('chairmanImageAlt')}
                                    className="absolute inset-0 h-[500px] w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Chairman'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>



                                {/* Decorative Corners - smaller for mobile */}
                                <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-[#BF943A] rounded-br-2xl"></div>
                                <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-[#BF943A] rounded-tl-2xl"></div>
                            </div>
                        </motion.div>

                        {/* Message */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp}
                            className="space-y-6"
                        >
                            <div className="space-y-4 text-gray-300">
                                <p className="text-[16px] md:text-[18px] leading-relaxed">
                                    {t('chairmanDesc1')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('chairmanDesc2')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('chairmanDesc3')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-300 font-semibold">
                                    {t('chairmanSignature')}
                                </p>
                            </div>
                        </motion.div>
                    </div>

                    {/* === DESKTOP / LG layout (image left | text right) === */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Left Side - Image */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInLeft}
                                className="relative order-1"
                            >
                                <div className="relative h-[500px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                    <img
                                        src={chairmanImage}
                                        alt={t('chairmanImageAlt')}
                                        className="h-[1000px] w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Chairman'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                    {/* Floating Badge */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.5, duration: 0.8 }}
                                        className="absolute top-8 left-8 bg-white/10 backdrop-blur-lg rounded-2xl px-6 py-4 border border-white/20"
                                    >
                                        <p className="text-[14px] uppercase tracking-wider text-[#BF943A] font-semibold mb-1">
                                            {t('chairmanBadge')}
                                        </p>
                                        <p className="text-[20px] font-bold text-white">{t('chairmanName')}</p>
                                    </motion.div>

                                    {/* Decorative Corners */}
                                    <div className="absolute bottom-6 right-6 w-20 h-20 border-b-2 border-r-2 border-[#BF943A] rounded-br-3xl"></div>
                                    <div className="absolute top-6 left-6 w-20 h-20 border-t-2 border-l-2 border-[#BF943A] rounded-tl-3xl"></div>
                                </div>
                            </motion.div>

                            {/* Right Side - Message */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInRight}
                                className="space-y-6 order-2"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                                    <span className="text-[16px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                        {t('chairmanLabel')}
                                    </span>
                                </div>

                                <h2 className="text-[44px] lg:text-[56px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                                    {t('chairmanTitle')}
                                </h2>

                                <div className="space-y-4 text-gray-300">
                                    <p className="text-[18px] lg:text-[20px] leading-relaxed">
                                        {t('chairmanDesc1')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('chairmanDesc2')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('chairmanDesc3')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-300 font-semibold">
                                        {t('chairmanSignature')}
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ===== OUR STAFF SECTION ===== */}
            <section className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24">
                <div className="container mx-auto px-4">

                    {/* === MOBILE / SM / MD layout (stacked: label → title → image → text) === */}
                    <div className="block lg:hidden space-y-8">
                        {/* Label */}
                        <div className="flex items-center gap-4">
                            <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                            <span className="text-[14px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                {t('staffLabel')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-[32px] sm:text-[40px] md:text-[48px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                            {t('staffTitle')}
                        </h2>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp} // assuming you have fadeInUp variant
                            className="relative rounded-2xl overflow-hidden shadow-2xl group"
                        >
                            <div className="relative aspect-[4/3] sm:aspect-[3/2] md:aspect-video">
                                <img
                                    src={staffImage}
                                    alt={t('staffImageAlt')}
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Our+Staff'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                                {/* Floating Info Card - adjusted for mobile */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4, duration: 0.7 }}
                                    className="absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/20"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[11px] sm:text-[12px] uppercase tracking-wider text-[#BF943A] font-semibold mb-1">
                                                {t('commitmentBadge')}
                                            </p>
                                            <p className="text-[18px] sm:text-[20px] font-bold text-white">{t('excellenceCare')}</p>
                                        </div>
                                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#BF943A] to-[#D4A574] flex items-center justify-center">
                                            <span className="text-[26px]">👥</span>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Decorative Corners - smaller for mobile */}
                                <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-[#BF943A] rounded-tr-2xl"></div>
                                <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-[#BF943A] rounded-bl-2xl"></div>
                            </div>
                        </motion.div>

                        {/* Description + Stats + Button */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInUp}
                            className="space-y-6"
                        >
                            <div className="space-y-4 text-gray-300">
                                <p className="text-[16px] md:text-[18px] leading-relaxed">
                                    {t('staffDesc1')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('staffDesc2')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('staffDesc3')}
                                </p>
                            </div>

                            <div className="pt-4 grid grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <div className="text-[32px] sm:text-[36px] font-bold text-[#BF943A]">{t('statStaff')}</div>
                                    <p className="text-[13px] sm:text-[15px] text-gray-400">{t('statStaffLabel')}</p>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-[32px] sm:text-[36px] font-bold text-[#96BA33]">{t('statSatisfaction')}</div>
                                    <p className="text-[13px] sm:text-[15px] text-gray-400">{t('statSatisfactionLabel')}</p>
                                </div>
                            </div>

                            <div className="pt-4 flex flex-wrap gap-4">
                                <a
                                    href="/beach/contact-us"
                                    className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-all duration-300 text-[14px] sm:text-[16px] uppercase tracking-wider"
                                >
                                    {t('contactUs')}
                                </a>
                            </div>
                        </motion.div>
                    </div>
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Left Side - Description */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInLeft}
                                className="space-y-6 order-2 lg:order-1"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="h-px w-[50px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                                    <span className="text-[16px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                        {t('staffLabel')}
                                    </span>
                                </div>

                                <h2 className="text-[48px] lg:text-[60px] font-bold leading-tight bg-gradient-to-r from-[#BF943A] via-[#D4A574] to-white bg-clip-text text-transparent">
                                    {t('staffTitle')}
                                </h2>

                                <div className="space-y-4 text-gray-300">
                                    <p className="text-[18px] lg:text-[20px] leading-relaxed">
                                        {t('staffDesc1')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('staffDesc2')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('staffDesc3')}
                                    </p>
                                </div>

                                <div className="pt-4 grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <div className="text-[42px] font-bold text-[#BF943A]">{t('statStaff')}</div>
                                        <p className="text-[16px] text-gray-400">{t('statStaffLabel')}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-[42px] font-bold text-[#96BA33]">{t('statSatisfaction')}</div>
                                        <p className="text-[16px] text-gray-400">{t('statSatisfactionLabel')}</p>
                                    </div>
                                </div>

                                <div className="pt-6 flex flex-wrap gap-4">
                                    <a
                                        href="/beach/contact-us"
                                        className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 text-[16px] uppercase tracking-wider"
                                    >
                                        {t('contactUs')}
                                    </a>
                                </div>
                            </motion.div>

                            {/* Right Side - Image */}
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInRight}
                                className="relative order-1 lg:order-2"
                            >
                                <div className="relative h-[500px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                    <img
                                        src={staffImage}
                                        alt={t('staffImageAlt')}
                                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Our+Staff'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                    {/* Floating Info Card */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.5, duration: 0.8 }}
                                        className="absolute bottom-8 left-8 right-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[14px] uppercase tracking-wider text-[#BF943A] font-semibold mb-1">
                                                    {t('commitmentBadge')}
                                                </p>
                                                <p className="text-[24px] font-bold text-white">{t('excellenceCare')}</p>
                                            </div>
                                            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#BF943A] to-[#D4A574] flex items-center justify-center">
                                                <span className="text-[32px]">👥</span>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Decorative Corner Elements */}
                                    <div className="absolute top-6 right-6 w-20 h-20 border-t-2 border-r-2 border-[#BF943A] rounded-tr-3xl"></div>
                                    <div className="absolute bottom-6 left-6 w-20 h-20 border-b-2 border-l-2 border-[#BF943A] rounded-bl-3xl"></div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ===== CONTACT CTA SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative w-full py-16 md:py-24 lg:py-32 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `
            linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(191, 148, 58, 0.2) 100%),
            url(${contactBgImage})
        `,
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="flex flex-col items-center justify-center text-center space-y-6 md:space-y-8">
                        {/* Label */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="flex items-center justify-center gap-3"
                        >
                            <div className="h-px w-[40px] bg-gradient-to-r from-[#BF943A] to-[#D4A574]"></div>
                            <span className="text-[12px] md:text-[14px] uppercase tracking-widest font-semibold text-[#BF943A]">
                                {t('ctaLabel')}
                            </span>
                            <div className="h-px w-[40px] bg-gradient-to-r from-[#D4A574] to-[#BF943A]"></div>
                        </motion.div>

                        {/* Main Heading */}
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="text-[32px] md:text-[48px] lg:text-[64px] font-bold leading-tight text-white max-w-4xl"
                        >
                            {t('ctaHeading')}
                        </motion.h2>

                        {/* Description */}
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-[16px] md:text-[18px] lg:text-[20px] text-gray-300 max-w-3xl leading-relaxed"
                        >
                            {t('ctaDesc')}
                        </motion.p>

                        {/* CTA Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6 w-full"
                        >
                            <a
                                href="/beach/contact-us"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF943A] to-[#D4A574] hover:from-[#D4A574] hover:to-[#BF943A] text-black font-bold px-10 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl text-[15px] md:text-[16px] uppercase tracking-wider"
                            >
                                {t('ctaButtonContact')}
                                <span>→</span>
                            </a>
                            <a
                                href="/beach/rooms"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-[#BF943A] bg-white text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-10 py-4 rounded-full transition-all duration-300 text-[15px] md:text-[16px] uppercase tracking-wider"
                            >
                                {t('ctaButtonRooms')}
                            </a>
                        </motion.div>

                        {/* Quick Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="pt-8 md:pt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 w-full"
                        >
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-[28px] md:text-[32px]">📞</span>
                                <p className="text-[12px] md:text-[13px] uppercase tracking-wider text-white font-semibold">
                                    {t('quickInfoCall')}
                                </p>
                                <p className="text-white text-[14px] md:text-[15px] font-medium">
                                    +94 34 227 8070
                                </p>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-[28px] md:text-[32px]">✉️</span>
                                <p className="text-[12px] md:text-[13px] uppercase tracking-wider text-white font-semibold">
                                    {t('quickInfoEmail')}
                                </p>
                                <p className="text-white text-[14px] md:text-[15px] font-medium">
                                    muthuabr@yahoo.com
                                </p>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-[28px] md:text-[32px]">📍</span>
                                <p className="text-[12px] md:text-[13px] uppercase tracking-wider text-white font-semibold">
                                    {t('quickInfoLocation')}
                                </p>
                                <p className="text-white text-[14px] md:text-[15px] font-medium">
                                    {t('locationCity')}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default About;